import { useRef, useState } from "react";

import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { Person } from "@/Common/Models/Person/Person";
import { AdministrativeCode } from "@/Common/Models/UI/Enums";
import { PopulationBasedFundingFormData, PopulationBasedFundingFormEnum } from "../../Models/PopulationBasedFunding/PopulationBasedFundingFormData";
import { ButtonControl } from "../Buttons/ButtonControl";
import { Modal } from "../Modals/Modal";
import { PopulationBasedFundingBasePopupBody } from "./PopulationBasedFundingBasePopupBody";

import "./PopulationBasedFundingRegistrationPopup.css";

interface IPopulationBasedFundingRegistrationPopupProps {
    currentStarredPhysician?: Person;
    onClose: () => void;
    onTrySubmit: (formData: PopulationBasedFundingFormData) => Promise<boolean>;
}

function PopulationBasedFundingRegistrationPopup(props: IPopulationBasedFundingRegistrationPopupProps): JSX.Element {
    const { getText } = useTranslation();
    const [hasSaveButtonBeenClicked, setHasSaveButtonBeenClicked] = useState<boolean>(false);
    const [formData, setFormData] = useState<PopulationBasedFundingFormData>(() => new PopulationBasedFundingFormData({
        SelectedPhysician: props.currentStarredPhysician,
        SelectedEffectiveDate: new Date(),
        Notes: "",
        AdministrativeCode: AdministrativeCode.Normal
    }));

    const isServerCallAlreadySent = useRef(false);

    const onSaveButtonClicked = async (): Promise<void> => {
        const onSave = async (): Promise<void> => {
            if (await props.onTrySubmit(formData)) {
                props.onClose();
            }
            else {
                isServerCallAlreadySent.current = false;
                setHasSaveButtonBeenClicked(false);
            }
        };

        if (!isServerCallAlreadySent.current) {
            isServerCallAlreadySent.current = true;
            await onSave();
        }

        setHasSaveButtonBeenClicked(true);
    };

    const isFormValid = (form: PopulationBasedFundingFormData): boolean => !hasSaveButtonBeenClicked && !!form.SelectedEffectiveDate && !!form.SelectedPhysician;

    const renderFooter = (): JSX.Element => (
        <div className="p-footer">
            <div className="footer-button-container">
                <ButtonControl
                    className="btn btn-default"
                    onClick={() => props.onClose()}
                    label={getText("CancelLabel")}
                />
                <ButtonControl
                    id="save_registration_butt"
                    className="btn btn-primary"
                    disabled={!isFormValid(formData)}
                    label={getText("SaveLabel")}
                    onClick={onSaveButtonClicked}
                />
            </div>
        </div>
    );

    return (
        <Modal
            id="PopulationBasedFundingRegistrationPopup"
            title={getText("PopulationBasedFunding_RegisterPatientLabel")}
            onClose={() => props.onClose()}
            top={50}
            width={485}
            draggable
            showOverlay={true}
            footer={renderFooter()}
            showX={true}
            roundTopCorner={true}
            disableOverlayClose={true}
        >
            <PopulationBasedFundingBasePopupBody
                formType={PopulationBasedFundingFormEnum.Register}
                formData={formData}
                onChange={setFormData}
            />
        </Modal>
    );
}

export { PopulationBasedFundingRegistrationPopup };
